import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, UserPlus, Inbox, Users, Briefcase, FileText, ShieldCheck, Star, Store, LineChart, CreditCard, Settings, IdCard, FileBadge, Send, Gift, UserRound,
  Building2, ListChecks, BadgeCheck, BarChart3, Wallet, LifeBuoy, ScrollText, Repeat, BellRing } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import DashboardLayout from "@/components/DashboardLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ComingSoon } from "@/components/Common";
import Home from "@/pages/Landing";
import FindCare from "@/pages/FindCare";
import CareResults from "@/pages/CareResults";
import Providers from "@/pages/Providers";
import Storefront from "@/pages/Storefront";
import Jobs from "@/pages/Jobs";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import { AuthCallback, VerifyEmail, ForgotPassword, ResetPassword } from "@/pages/AuthPages";
import ConsumerDashboard from "@/pages/ConsumerDashboard";
import { AcceptingToggle, ProviderOverview, ProviderRequests, ProviderApplicants, ProviderReviews, ProviderAnalytics, ProviderBilling } from "@/pages/provider/ProviderPages";
import ProviderProfile from "@/pages/provider/ProviderProfile";
import ProviderJobs from "@/pages/provider/ProviderJobs";
import { WorkerOverview, WorkerProfile, WorkerPassport, WorkerCredentials, WorkerApplications, WorkerOffers } from "@/pages/worker/WorkerPages";
import * as A from "@/pages/admin/AdminPages";
import AdminCareMatch from "@/pages/admin/AdminCareMatch";
import CareMatchDetail from "@/pages/CareMatchDetail";
import CareMatchUnsubscribe from "@/pages/CareMatchUnsubscribe";

const PROVIDER_NAV = [
  ["", "Dashboard", LayoutDashboard], ["leads", "Client Leads", UserPlus], ["care-requests", "Care Requests", Inbox], ["workforce", "Workforce", Users], ["jobs", "Jobs", Briefcase],
  ["applicants", "Applicants", FileText], ["credentials", "Credentials", ShieldCheck], ["reviews", "Reviews", Star], ["profile", "Profile", Store], ["analytics", "Analytics", LineChart],
  ["billing", "Billing", CreditCard], ["settings", "Settings", Settings]];
const WORKER_NAV = [["", "Dashboard", LayoutDashboard], ["passport", "Zunvel Passport", IdCard], ["profile", "Profile", UserRound], ["credentials", "Credentials", FileBadge],
  ["jobs", "Jobs", Briefcase], ["applications", "Applications", Send], ["offers", "Job Offers", Gift]];
const ADMIN_NAV = [["", "Overview", LayoutDashboard], ["consumers", "Consumers", Users], ["providers", "Providers", Building2], ["workers", "Workers", UserRound], ["care-requests", "Care Requests", Inbox], ["carematch", "CareMatch", BellRing],
  ["jobs", "Jobs", Briefcase], ["credential-reviews", "Credential Reviews", FileBadge], ["verification-queue", "Verification Queue", BadgeCheck], ["reviews", "Reviews", Star],
  ["reports", "Reports", BarChart3], ["subscriptions", "Subscriptions", Repeat], ["payments", "Payments", Wallet], ["support", "Support", LifeBuoy], ["audit-logs", "Audit Logs", ScrollText],
  ["settings", "Platform Settings", ListChecks]];
const items = (base, nav) => nav.map(([p, label, icon]) => ({ to: p ? `${base}/${p}` : base, label, icon, end: !p }));

const Public = () => (<><SiteHeader /><main className="min-h-[70vh]"><Outlet /></main><SiteFooter /></>);
const AdminTag = () => <span className="text-xs font-bold uppercase tracking-wider text-teal-700 bg-teal-50 rounded-full px-2.5 py-1">MFA secured</span>;

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<Public />}>
        <Route path="/" element={<Home />} />
        <Route path="/welcome" element={<Home />} />
        <Route path="/find-care" element={<FindCare />} />
        <Route path="/find-care/results/:id" element={<CareResults />} />
        <Route path="/providers" element={<Providers />} />
        <Route path="/providers/:slug" element={<Storefront />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/account" element={<ProtectedRoute role="consumer"><ConsumerDashboard /></ProtectedRoute>} />
        <Route path="/account/carematch/:id" element={<ProtectedRoute role="consumer"><CareMatchDetail /></ProtectedRoute>} />
        <Route path="/carematch/unsubscribe" element={<CareMatchUnsubscribe />} />
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/provider" element={<ProtectedRoute role="provider"><DashboardLayout title="Provider Portal" items={items("/provider", PROVIDER_NAV)} accent={<AcceptingToggle />} /></ProtectedRoute>}>
        <Route index element={<ProviderOverview />} />
        <Route path="leads" element={<ProviderRequests leadsOnly />} />
        <Route path="care-requests" element={<ProviderRequests />} />
        <Route path="workforce" element={<ComingSoon title="Workforce" body="Manage your hired Zunvel workers, schedules and credential compliance. Planned for the next milestone." />} />
        <Route path="jobs" element={<ProviderJobs />} />
        <Route path="applicants" element={<ProviderApplicants />} />
        <Route path="credentials" element={<ComingSoon title="Credentials" body="Track your workforce's credential expirations in one place. Today you can inspect each applicant's Passport from Applicants." />} />
        <Route path="reviews" element={<ProviderReviews />} />
        <Route path="profile" element={<ProviderProfile />} />
        <Route path="analytics" element={<ProviderAnalytics />} />
        <Route path="billing" element={<ProviderBilling />} />
        <Route path="settings" element={<ComingSoon title="Settings" body="Team members, notification preferences and multi-location management are planned." />} />
      </Route>
      <Route path="/worker" element={<ProtectedRoute role="worker"><DashboardLayout title="Worker Portal" items={items("/worker", WORKER_NAV)} /></ProtectedRoute>}>
        <Route index element={<WorkerOverview />} />
        <Route path="passport" element={<WorkerPassport />} />
        <Route path="profile" element={<WorkerProfile />} />
        <Route path="credentials" element={<WorkerCredentials />} />
        <Route path="jobs" element={<Jobs />} />
        <Route path="applications" element={<WorkerApplications />} />
        <Route path="offers" element={<WorkerOffers />} />
      </Route>
      <Route path="/admin" element={<ProtectedRoute role="admin"><DashboardLayout title="Zunvel Admin" items={items("/admin", ADMIN_NAV)} accent={<AdminTag />} /></ProtectedRoute>}>
        <Route index element={<A.AdminOverview />} />
        <Route path="consumers" element={<A.AdminUsers role="consumer" title="Consumers" />} />
        <Route path="providers" element={<A.AdminProviders />} />
        <Route path="workers" element={<A.AdminWorkers />} />
        <Route path="care-requests" element={<A.AdminCareRequests />} />
        <Route path="carematch" element={<AdminCareMatch />} />
        <Route path="jobs" element={<A.AdminJobs />} />
        <Route path="credential-reviews" element={<A.AdminCredentialReviews />} />
        <Route path="verification-queue" element={<A.AdminVerificationQueue />} />
        <Route path="reviews" element={<A.AdminReviews />} />
        <Route path="reports" element={<ComingSoon title="Reports" />} />
        <Route path="subscriptions" element={<A.AdminSubscriptions />} />
        <Route path="payments" element={<ComingSoon title="Payments" body="Stripe-ready payment records exist in the data model. Live payments are NOT active and will only be enabled when instructed." />} />
        <Route path="support" element={<ComingSoon title="Support" />} />
        <Route path="audit-logs" element={<A.AdminAuditLogs />} />
        <Route path="settings" element={<A.AdminSettings />} />
      </Route>
      <Route path="*" element={<Public />}><Route path="*" element={<div className="max-w-3xl mx-auto py-24 px-4 text-center"><h1 className="text-3xl font-bold">Page not found</h1></div>} /></Route>
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
